import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  UserPlus,
  MoreVertical,
  Mail,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  Users,
  Filter,
  AlertTriangle,
  Building2,
  Phone,
  FileText,
  Loader2,
  Pencil,
  ClipboardList,
  Key,
  RefreshCcw,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { IntakeClient, IntakeClientStatus, RiskLevel, CreateIntakeClientInput } from '@/types/intake';

export function IntakeClientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<IntakeClientStatus | 'all'>(
    (searchParams.get('status') as IntakeClientStatus) || 'all'
  );
  const [riskFilter, setRiskFilter] = useState<RiskLevel | 'all'>('all');
  const [selectedClient, setSelectedClient] = useState<IntakeClient | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<IntakeClient | null>(null);

  // Create form state
  const [createForm, setCreateForm] = useState<CreateIntakeClientInput>({
    name: '',
    email: '',
    phone: '',
    company: '',
    abn: '',
    sendWelcomeEmail: true,
    tempPassword: '',
  });
  const [showTempPassword, setShowTempPassword] = useState(false);

  // Fetch clients
  const { data: clientsData, isLoading, refetch } = useQuery({
    queryKey: ['intake-clients', search, statusFilter, riskFilter],
    queryFn: () => intakeService.listClients({
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      riskLevel: riskFilter !== 'all' ? riskFilter : undefined,
    }),
  });

  const clients = clientsData?.clients || [];

  // Generate random password
  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Ensure complexity
    password = password.substring(0, 8) + 'Aa1!';
    setCreateForm({ ...createForm, tempPassword: password });
  };

  // Create client mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateIntakeClientInput) => intakeService.createClient(input),
    onSuccess: (data) => {
      const message = createForm.sendWelcomeEmail
        ? 'Client created successfully! Welcome email has been sent.'
        : `Client created! Temp password: ${createForm.tempPassword || 'auto-generated'}`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['intake-clients'] });
      setCreateDialogOpen(false);
      setCreateForm({ name: '', email: '', phone: '', company: '', abn: '', sendWelcomeEmail: true, tempPassword: '' });
      setShowTempPassword(false);
    },
    onError: () => {
      toast.error('Failed to create client');
    },
  });

  // Delete client mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => intakeService.deleteClient(id),
    onSuccess: () => {
      toast.success('Client deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['intake-clients'] });
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      if (selectedClient?.id === clientToDelete?.id) {
        setSelectedClient(null);
      }
    },
    onError: () => {
      toast.error('Failed to delete client');
    },
  });

  // Resend welcome email mutation
  const resendEmailMutation = useMutation({
    mutationFn: (clientId: string) => intakeService.resendWelcomeEmail(clientId),
    onSuccess: () => {
      toast.success('Welcome email sent successfully');
    },
    onError: () => {
      toast.error('Failed to send email');
    },
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    setSearchParams(prev => {
      if (value) prev.set('search', value);
      else prev.delete('search');
      return prev;
    });
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value as IntakeClientStatus | 'all');
    setSearchParams(prev => {
      if (value !== 'all') prev.set('status', value);
      else prev.delete('status');
      return prev;
    });
  };

  const getStatusBadge = (status: IntakeClientStatus) => {
    const config: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-gray-500/10 text-gray-500' },
      active: { label: 'Active', className: 'bg-blue-500/10 text-blue-500' },
      submitted: { label: 'Submitted', className: 'bg-purple-500/10 text-purple-500' },
      under_review: { label: 'Under Review', className: 'bg-amber-500/10 text-amber-500' },
      approved: { label: 'Approved', className: 'bg-green-500/10 text-green-500' },
      rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-500' },
      archived: { label: 'Archived', className: 'bg-gray-500/10 text-gray-500' },
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const getRiskBadge = (level?: RiskLevel) => {
    if (!level) return null;
    const config: Record<string, { label: string; className: string }> = {
      low: { label: 'Low', className: 'bg-green-500/10 text-green-500' },
      medium: { label: 'Medium', className: 'bg-yellow-500/10 text-yellow-500' },
      high: { label: 'High', className: 'bg-orange-500/10 text-orange-500' },
      critical: { label: 'Critical', className: 'bg-red-500/10 text-red-500' },
    };
    const { label, className } = config[level] || config.low;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Intake Clients</h1>
          <p className="text-muted-foreground">
            Manage client onboarding and document collection
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={riskFilter} onValueChange={(v) => setRiskFilter(v as RiskLevel | 'all')}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risks</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        {/* Clients List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div>
              <CardTitle className="text-base">Clients</CardTitle>
              <CardDescription>{clients.length} clients found</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium">No clients found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {search || statusFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Add your first client to get started'
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className={cn(
                      'flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors',
                      selectedClient?.id === client.id && 'bg-muted/50'
                    )}
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{client.name}</p>
                        {client.riskLevel && getRiskBadge(client.riskLevel)}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                      {client.company && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {client.company}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="text-right hidden sm:block">
                        <Progress value={client.intakeProgress} className="w-20 h-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {client.intakeProgress}%
                        </p>
                      </div>
                      {getStatusBadge(client.status)}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/panel/intake/clients/${client.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/panel/intake/clients/${client.id}/edit`)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Client
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/panel/intake/clients/${client.id}/documents`)}>
                            <ClipboardList className="mr-2 h-4 w-4" />
                            Set Documents
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => resendEmailMutation.mutate(client.id)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Resend Welcome Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setClientToDelete(client);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Details Panel */}
        <Card className="h-fit sticky top-20">
          <CardHeader>
            <CardTitle className="text-base">Client Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedClient ? (
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{selectedClient.name}</h3>
                    {getStatusBadge(selectedClient.status)}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${selectedClient.email}`} className="hover:text-primary">
                        {selectedClient.email}
                      </a>
                    </div>
                    {selectedClient.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {selectedClient.phone}
                      </div>
                    )}
                    {selectedClient.company && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        {selectedClient.company}
                      </div>
                    )}
                    {selectedClient.abn && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        ABN: {selectedClient.abn}
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Intake Progress</span>
                    <span className="font-medium">{selectedClient.intakeProgress}%</span>
                  </div>
                  <Progress value={selectedClient.intakeProgress} className="h-2" />
                </div>

                {/* Risk */}
                {selectedClient.riskLevel && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Risk Assessment</span>
                    {getRiskBadge(selectedClient.riskLevel)}
                  </div>
                )}

                <Separator />

                {/* Dates */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span>{formatDate(selectedClient.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Login</span>
                    <span>{formatDate(selectedClient.lastLoginAt)}</span>
                  </div>
                  {selectedClient.submittedAt && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Submitted</span>
                      <span>{formatDate(selectedClient.submittedAt)}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedClient.notes && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <span className="text-sm font-medium">Notes</span>
                      <p className="text-sm text-muted-foreground">{selectedClient.notes}</p>
                    </div>
                  </>
                )}

                <Separator />

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => navigate(`/panel/intake/clients/${selectedClient.id}`)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Full Details
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a client to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client for intake. They will receive a welcome email with login credentials.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="John Smith"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                placeholder="+61 4XX XXX XXX"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                placeholder="Company Pty Ltd"
                value={createForm.company}
                onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="abn">ABN</Label>
              <Input
                id="abn"
                placeholder="XX XXX XXX XXX"
                value={createForm.abn}
                onChange={(e) => setCreateForm({ ...createForm, abn: e.target.value })}
              />
            </div>

            <Separator />

            {/* Password Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="tempPassword">Temporary Password</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateRandomPassword}
                  className="h-7 text-xs"
                >
                  <RefreshCcw className="mr-1 h-3 w-3" />
                  Generate
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="tempPassword"
                  type={showTempPassword ? 'text' : 'password'}
                  placeholder="Leave blank to auto-generate"
                  value={createForm.tempPassword}
                  onChange={(e) => setCreateForm({ ...createForm, tempPassword: e.target.value })}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-10"
                  onClick={() => setShowTempPassword(!showTempPassword)}
                >
                  {showTempPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Client will be required to change this password on first login.
              </p>
            </div>

            {/* Send Welcome Email Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendWelcomeEmail"
                checked={createForm.sendWelcomeEmail}
                onCheckedChange={(checked) =>
                  setCreateForm({ ...createForm, sendWelcomeEmail: checked as boolean })
                }
              />
              <Label htmlFor="sendWelcomeEmail" className="text-sm font-normal cursor-pointer">
                Send welcome email with login credentials
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate(createForm)}
              disabled={!createForm.name || !createForm.email || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Client
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{clientToDelete?.name}</strong>? This action cannot be undone and will remove all associated documents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => clientToDelete && deleteMutation.mutate(clientToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
